import { Button, type ButtonProps } from '@heidi/design-system/button';
import { Kbd } from '@heidi/design-system/kbd';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn, isMac } from '@heidi/utils';
import { TooltipPortal } from '@radix-ui/react-tooltip';
import { type ButtonHTMLAttributes, type HTMLProps, forwardRef } from 'react';
import type { Key } from 'ts-key-enum';
import { Surface } from './surface';

export type ToolbarWrapperProps = {
  shouldShowContent?: boolean;
  isVertical?: boolean;
} & HTMLProps<HTMLDivElement>;

export const ToolbarWrapper = forwardRef<HTMLDivElement, ToolbarWrapperProps>(
  (
    { shouldShowContent = true, children, isVertical = false, className, ...rest },
    ref
  ) => {
    const toolbarClassName = cn(
      'inline-flex h-full gap-0.5 leading-none text-text-primary',
      isVertical ? 'flex-col p-2' : 'flex-row items-center p-1',
      className
    );

    return (
      shouldShowContent && (
        <Surface
          data-testid="toolbar-wrapper"
          className={toolbarClassName}
          {...rest}
          ref={ref}
        >
          {children}
        </Surface>
      )
    );
  }
);

ToolbarWrapper.displayName = 'Toolbar';

type shortcutKey = { type: 'modifier'; key: Key } | { type: 'letter'; key: string };

export type ToolbarButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  activeClassName?: string;
  tooltip?: string;
  tooltipShortcuts?: shortcutKey[];
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  iconOnly?: boolean;
};

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      children,
      size = 'xs',
      variant = 'ghost',
      activeClassName = 'bg-background-secondary',
      className,
      active,
      tooltip,
      tooltipShortcuts,
      iconOnly = true,
      ...rest
    },
    ref
  ) => {
    const buttonClass = cn('gap-1 p-1', className);

    if (
      (tooltipShortcuts === undefined ||
        tooltipShortcuts === null ||
        tooltipShortcuts.length === 0) &&
      !tooltip
    ) {
      return (
        <Button
          activeClassName={active ? activeClassName : undefined}
          className={buttonClass}
          variant={variant}
          size={size}
          ref={ref}
          {...rest}
          iconOnly={iconOnly}
        >
          {children}
        </Button>
      );
    }

    const button = (
      <Button
        activeClassName={active ? activeClassName : undefined}
        className={buttonClass}
        variant={variant}
        size={size}
        ref={ref}
        {...rest}
        iconOnly={iconOnly}
      >
        {children}
      </Button>
    );

    return (
      // disableHoverableContent: dismiss as soon as the pointer leaves the
      // button. Without it, moving towards a button on the row above passes
      // through the tooltip, which keeps it open and swallows the hover.
      <Tooltip disableHoverableContent>
        <TooltipTrigger asChild>
          {rest.disabled ? (
            // Browsers swallow pointer events on a natively disabled button,
            // which would keep the tooltip from ever opening — the span
            // carries the hover instead (same wrapper the link popover uses).
            <span className="inline-flex">{button}</span>
          ) : (
            button
          )}
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            sideOffset={8}
            className="pointer-events-none flex h-[38px] items-center gap-1 py-0 text-text-primary-invert"
          >
            {tooltip}
            {tooltipShortcuts?.map((shortcut, index) =>
              shortcut.type === 'modifier' ? (
                <Kbd
                  key={index}
                  isMac={isMac}
                  keyType="modifier"
                  contentClassName="text-text-primary-invert"
                >
                  {shortcut.key}
                </Kbd>
              ) : (
                <Kbd
                  key={index}
                  isMac={isMac}
                  contentClassName="text-text-primary-invert"
                >
                  {shortcut.key}
                </Kbd>
              )
            )}
            <TooltipArrow />
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  }
);

ToolbarButton.displayName = 'ToolbarButton';
